import React from 'react';
import {
  Box,
  Button,
  Card,
  Chip,
  Collapse,
  Container,
  Grid,
  Stack,
  Typography,
} from '@mui/material';
import { alpha } from '@mui/material/styles';
import {
  ArrowForwardRounded as ArrowForwardIcon,
  BlockOutlined as CannotIcon,
  CheckCircleOutlineRounded as CanIcon,
  ExpandMoreRounded as ExpandMoreIcon,
  InfoOutlined as InfoIcon,
  LockOutlined as LockIcon,
} from '@mui/icons-material';
import colors from '../../assets/theme/colors';
import { getRoleColor } from '../../assets/theme/roleColors';
import { ROLE_GUIDE_COPY, ROLE_PERMISSION_GUIDE_ITEMS } from './rolePermissionsGuideData';

export { ROLE_PERMISSION_GUIDE_ITEMS };

const surfaceSx = {
  borderRadius: 2,
  bgcolor: colors.landing.surface,
  border: `1px solid ${colors.landing.borderSoft}`,
  boxShadow: `0 6px 18px ${alpha(colors.landing.deepNavy, 0.05)}`,
  width: '100%',
};

const SectionList = ({ title, items, icon: Icon, color }) => (
  <Box>
    <Typography sx={{ fontSize: '0.74rem', fontWeight: 800, letterSpacing: '0.08em', color, textTransform: 'uppercase', mb: 1 }}>
      {title}
    </Typography>
    <Stack spacing={0.75}>
      {items.map((item) => (
        <Box key={item} sx={{ display: 'grid', gridTemplateColumns: '18px 1fr', gap: 0.85, alignItems: 'start' }}>
          <Icon sx={{ fontSize: 17, color, mt: 0.15 }} />
          <Typography sx={{ color: colors.landing.bodyText, fontSize: '0.92rem', lineHeight: 1.35 }}>
            {item}
          </Typography>
        </Box>
      ))}
    </Stack>
  </Box>
);

const EXAMPLE_ROLE_COLORS = {
  'Care Partner': 'carePartner',
  Caregiver: 'caregiver',
  Specialist: 'therapist',
};

const RolePermissionCard = ({ item, expanded, onToggle, onSelect }) => {
  const roleColor = getRoleColor(item.roleKey, 'primary');
  const softColor = alpha(roleColor, 0.1);
  const Icon = item.icon;

  return (
    <Card
      elevation={0}
      sx={{
        ...surfaceSx,
        height: '100%',
        p: { xs: 2.1, sm: 2.4 },
        display: 'flex',
        flexDirection: 'column',
        transition: 'box-shadow 160ms ease, border-color 160ms ease',
        '&:hover': {
          borderColor: alpha(roleColor, 0.3),
          boxShadow: `0 8px 20px ${alpha(roleColor, 0.08)}`,
        },
        '&:focus-within': {
          borderColor: roleColor,
          boxShadow: `0 0 0 3px ${alpha(roleColor, 0.12)}`,
        },
      }}
    >
      <Stack spacing={2.1} sx={{ flex: 1 }}>
        <Stack direction="row" spacing={1.6} alignItems="flex-start">
          <Box
            sx={{
              width: 56,
              height: 56,
              borderRadius: 2,
              display: 'grid',
              placeItems: 'center',
              bgcolor: softColor,
              color: roleColor,
              flexShrink: 0,
            }}
          >
            <Icon sx={{ fontSize: 30 }} />
          </Box>
          <Box sx={{ minWidth: 0, flex: 1 }}>
            <Typography variant="h5" sx={{ color: colors.landing.heroText, fontWeight: 850, lineHeight: 1.05, fontSize: { xs: '1.35rem', md: '1.45rem' } }}>
              {item.title}
            </Typography>
            <Chip
              label={item.badge}
              size="small"
              sx={{ mt: 1, height: 26, borderRadius: 99, bgcolor: softColor, color: roleColor, fontWeight: 800 }}
            />
          </Box>
        </Stack>

        <Typography sx={{ color: colors.landing.bodyText, lineHeight: 1.45, fontSize: '0.98rem' }}>
          {item.description}
        </Typography>

        <SectionList title="Can" items={item.can} icon={CanIcon} color={colors.semantic.sageDeep} />
        <SectionList title="Cannot" items={item.cannot} icon={CannotIcon} color={colors.semantic.ink2} />

        {item.note ? (
          <Box sx={{ p: 1.4, borderRadius: 1.5, bgcolor: alpha(colors.semantic.warning, 0.08), border: `1px solid ${alpha(colors.semantic.warning, 0.18)}` }}>
            <Stack direction="row" spacing={1} alignItems="flex-start">
              <LockIcon sx={{ fontSize: 18, color: colors.semantic.warning, mt: 0.1 }} />
              <Typography sx={{ color: colors.landing.bodyText, fontSize: '0.9rem', lineHeight: 1.35, fontWeight: 650 }}>
                {item.note}
              </Typography>
            </Stack>
          </Box>
        ) : null}

        <Box>
          <Typography sx={{ fontSize: '0.74rem', fontWeight: 800, letterSpacing: '0.08em', color: colors.landing.textMuted, textTransform: 'uppercase', mb: 1 }}>
            Best for
          </Typography>
          <Stack direction="row" gap={0.75} flexWrap="wrap">
            {item.bestFor.map((label) => (
              <Chip key={label} label={label} size="small" sx={{ bgcolor: colors.semantic.profileFlow.notStartedBg, color: colors.landing.bodyText, fontWeight: 650 }} />
            ))}
          </Stack>
        </Box>

        <Box sx={{ p: 1.4, borderRadius: 1.5, bgcolor: alpha(roleColor, 0.06), color: colors.landing.bodyText }}>
          <Typography sx={{ fontSize: '0.78rem', fontWeight: 800, color: roleColor, mb: 0.35, textTransform: 'uppercase', letterSpacing: '0.06em' }}>
            Choose this if
          </Typography>
          <Typography sx={{ fontSize: '0.92rem', lineHeight: 1.35 }}>{item.chooseThisIf}</Typography>
        </Box>

        <Box>
          <Button
            type="button"
            onClick={onToggle}
            aria-expanded={expanded}
            endIcon={<ExpandMoreIcon sx={{ transform: expanded ? 'rotate(180deg)' : 'rotate(0deg)', transition: 'transform 160ms ease' }} />}
            sx={{ color: roleColor, textTransform: 'none', fontWeight: 800, px: 0 }}
          >
            Learn more
          </Button>
          <Collapse in={expanded}>
            <Typography sx={{ color: colors.landing.textMuted, fontSize: '0.9rem', lineHeight: 1.45, pb: 1 }}>
              {item.learnMore}
            </Typography>
          </Collapse>
        </Box>
      </Stack>

      <Button
        type="button"
        fullWidth
        onClick={() => onSelect(item)}
        endIcon={<ArrowForwardIcon />}
        sx={{
          mt: 2.2,
          py: 1.15,
          borderRadius: 1.5,
          bgcolor: roleColor,
          color: colors.semantic.surface,
          textTransform: 'none',
          fontWeight: 850,
          boxShadow: 'none',
          '&:hover': { bgcolor: roleColor, boxShadow: 'none' },
        }}
      >
        Invite {item.title}
      </Button>
    </Card>
  );
};

const RolePermissionsGuide = ({
  roleOptions = ROLE_PERMISSION_GUIDE_ITEMS,
  guideCopy = ROLE_GUIDE_COPY.default,
  onRoleSelect,
}) => {
  const [expandedRole, setExpandedRole] = React.useState(null);
  const resolvedGuideCopy = guideCopy || ROLE_GUIDE_COPY.default;

  return (
    <Container maxWidth="xl" sx={{ py: { xs: 2.5, md: 5 }, px: { xs: 1.6, sm: 3 } }}>
      <Box sx={{ textAlign: 'center', maxWidth: 760, mx: 'auto', mb: { xs: 3, md: 4.5 } }}>
        <Typography variant="h3" sx={{ color: colors.landing.heroText, fontWeight: 850, letterSpacing: 0, fontSize: { xs: '2rem', sm: '2.45rem', md: '3.2rem' } }}>
          Who Can Do What
        </Typography>
        <Typography sx={{ mt: 1.25, color: colors.landing.bodyText, fontSize: { xs: '1rem', sm: '1.08rem' }, lineHeight: 1.5 }}>
          {resolvedGuideCopy.intro}
        </Typography>
      </Box>

      <Grid container spacing={{ xs: 2, md: 2.5 }} alignItems="stretch">
        {roleOptions.map((item) => (
          <Grid item xs={12} sm={4} key={item.role} sx={{ display: 'flex' }}>
            <RolePermissionCard
              item={item}
              expanded={expandedRole === item.role}
              onToggle={() => setExpandedRole((current) => (current === item.role ? null : item.role))}
              onSelect={onRoleSelect}
            />
          </Grid>
        ))}
      </Grid>

      <Grid container spacing={2.5} sx={{ mt: { xs: 2.5, md: 3.5 } }}>
        <Grid item xs={12} md={5}>
          <Box sx={{ ...surfaceSx, p: { xs: 2, sm: 2.6 }, bgcolor: colors.semantic.profileFlow.heroGradientEnd }}>
            <Stack direction="row" spacing={1.2} alignItems="flex-start">
              <LockIcon sx={{ color: colors.semantic.sageDeep, mt: 0.2 }} />
              <Box>
                <Typography variant="h6" sx={{ color: colors.landing.heroText, fontWeight: 850, mb: 0.8 }}>
                  Medication Safety
                </Typography>
                <Typography sx={{ color: colors.landing.bodyText, lineHeight: 1.45 }}>
                  Care Owners and Care Partners can add, edit, pause, or remove medication definitions.
                </Typography>
                <Typography sx={{ mt: 1, color: colors.landing.textMuted, lineHeight: 1.45 }}>
                  Caregivers can still log medication doses.
                </Typography>
              </Box>
            </Stack>
          </Box>
        </Grid>
        <Grid item xs={12} md={7}>
          <Box sx={{ ...surfaceSx, p: { xs: 2, sm: 2.6 } }}>
            <Stack direction="row" spacing={1.2} alignItems="flex-start" sx={{ mb: 1.4 }}>
              <InfoIcon sx={{ color: colors.semantic.sageDeep, mt: 0.2 }} />
              <Typography variant="h6" sx={{ color: colors.landing.heroText, fontWeight: 850 }}>
                {resolvedGuideCopy.examplesTitle}
              </Typography>
            </Stack>
            <Stack spacing={1}>
              {resolvedGuideCopy.examples.map(([example, role]) => (
                <Box key={example} sx={{ display: 'flex', gap: 1, flexWrap: 'wrap', alignItems: 'center', color: colors.landing.bodyText }}>
                  <Typography sx={{ fontSize: '0.95rem', lineHeight: 1.35 }}>{example}</Typography>
                  <ArrowForwardIcon sx={{ fontSize: 18, color: colors.landing.textMuted }} />
                  <Chip
                    label={role}
                    size="small"
                    sx={{
                      bgcolor: alpha(getRoleColor(EXAMPLE_ROLE_COLORS[role], 'primary'), 0.12),
                      color: getRoleColor(EXAMPLE_ROLE_COLORS[role], 'accent'),
                      border: `1px solid ${alpha(getRoleColor(EXAMPLE_ROLE_COLORS[role], 'primary'), 0.22)}`,
                      fontWeight: 800,
                    }}
                  />
                </Box>
              ))}
            </Stack>
            <Typography sx={{ mt: 1.6, color: colors.landing.textMuted, lineHeight: 1.45 }}>
              {resolvedGuideCopy.footer}
            </Typography>
          </Box>
        </Grid>
      </Grid>
    </Container>
  );
};

export default RolePermissionsGuide;
